import { Component } from '@theme/component';
import { lockScroll, unlockScroll } from '@theme/utilities';

/**
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details
 * @property {HTMLElement} trigger
 * @property {HTMLDialogElement} panel
 * @property {HTMLElement} heading
 * @property {HTMLElement} [signup]
 * @property {HTMLElement} [success]
 * @property {HTMLElement} [code]
 * @property {HTMLElement} [status]
 * @property {HTMLAnchorElement} [shopLink]
 * @property {HTMLInputElement} [email]
 * @property {HTMLButtonElement} [copyButton]
 * @property {HTMLElement} [description]
 * @property {HTMLElement} [successText]
 * @property {HTMLElement} [formErrors]
 * @extends Component<Refs>
 */
export class MervauxOffer extends Component {
  requiredRefs = ['details', 'trigger', 'panel', 'heading'];
  #controller;
  #autoTimer;
  #expiryTimer;
  #copyTimer;
  #key;
  #expires;
  #autoOpened = false;
  #modalOpen = false;
  #returnFocus;
  #backdropPointerDown = false;

  connectedCallback() {
    super.connectedCallback();
    this.#initialize();
  }

  updatedCallback() {
    this.#cleanup();
    super.updatedCallback();
    this.#initialize();
  }

  disconnectedCallback() {
    this.#cleanup();
    super.disconnectedCallback();
  }

  #cleanup() {
    this.#controller?.abort();
    clearTimeout(this.#autoTimer);
    clearTimeout(this.#expiryTimer);
    clearTimeout(this.#copyTimer);
    if (this.#modalOpen) this.refs.panel.close();
    if (this.refs.panel) unlockScroll(this.refs.panel);
    this.#modalOpen = false;
    this.#backdropPointerDown = false;
  }

  #read() {
    try { return JSON.parse(localStorage.getItem(this.#key) || '{}'); } catch { return {}; }
  }

  #save(values) {
    try { localStorage.setItem(this.#key, JSON.stringify({ ...this.#read(), ...values })); } catch {}
  }

  #initialize() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.#expires = Date.parse(this.dataset.expires || '');
    this.#key = `mervaux-offer:${this.dataset.campaign || this.dataset.expires}`;
    // The open attribute supports the details fallback before this module loads.
    this.refs.panel.removeAttribute('open');
    this.refs.trigger.setAttribute('aria-haspopup', 'dialog');
    this.refs.trigger.setAttribute('aria-expanded', String(this.refs.details.open));
    if (!this.#checkExpiry()) return;
    this.hidden = false;

    const remembered = this.#read();
    const confirmed = this.refs.success?.dataset.confirmed === 'true' || this.dataset.knownSubscriber === 'true';
    const { signup, success, code: codeElement, shopLink } = this.refs;
    // An app block owns its fields, consent and success state. Keep the native
    // customer-form restoration only for the section's no-app fallback.
    if (signup && success && codeElement && shopLink) {
      if (confirmed && codeElement.textContent.trim()) {
        this.#save({ subscribed: true, code: codeElement.textContent.trim() });
      } else if (remembered.subscribed && typeof remembered.code === 'string') {
        codeElement.textContent = remembered.code;
        success.hidden = false;
        signup.hidden = true;
      }
      if (!success.hidden) {
        if (this.refs.description) this.refs.description.hidden = true;
        const code = codeElement.textContent.trim();
        const destination = new URL(shopLink.href, location.origin);
        if (destination.origin === location.origin && code) {
          shopLink.href = `/discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(destination.pathname + destination.search)}`;
        }
      }
    }

    this.refs.details.addEventListener('toggle', () => {
      if (this.refs.details.open) {
        this.#openPanel();
      } else if (this.#modalOpen) {
        this.closeOffer();
      }
    }, { signal });

    this.refs.panel.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.closeOffer();
    }, { signal });
    this.refs.panel.addEventListener('close', () => {
      if (!this.refs.panel.open && this.#modalOpen) this.closeOffer();
    }, { signal });
    this.refs.panel.addEventListener('keydown', (event) => {
      // Native modal focus containment includes app-owned shadow DOM controls.
      // A light-DOM-only trap would skip those fields and prevent keyboard use.
      if (event.key !== 'Tab' || this.dataset.formProvider === 'app') return;
      const focusable = [...this.refs.panel.querySelectorAll('a[href], button, input, select, textarea, [tabindex]')]
        .filter((node) => node instanceof HTMLElement && node.tabIndex >= 0 && !node.matches(':disabled') && node.getClientRects().length > 0 && getComputedStyle(node).visibility !== 'hidden');
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) { event.preventDefault(); this.refs.heading.focus(); return; }
      if (event.shiftKey && (document.activeElement === first || !focusable.includes(document.activeElement))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !focusable.includes(document.activeElement))) {
        event.preventDefault();
        first.focus();
      }
    }, { signal });
    this.refs.panel.addEventListener('pointerdown', (event) => {
      this.#backdropPointerDown = this.#isBackdrop(event);
    }, { signal });
    this.refs.panel.addEventListener('click', (event) => {
      if (this.#backdropPointerDown && this.#isBackdrop(event)) this.closeOffer();
      this.#backdropPointerDown = false;
    }, { signal });
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) this.#checkExpiry();
    }, { signal });
    window.addEventListener('pageshow', () => this.#checkExpiry(), { signal });

    const form = signup?.closest('form');
    form?.addEventListener('submit', (event) => {
      if (!this.#checkExpiry()) { event.preventDefault(); return; }
      try { sessionStorage.setItem(`${this.#key}:pending`, form.id); } catch {}
    }, { signal });

    let pending = false;
    try {
      pending = Boolean(form && sessionStorage.getItem(`${this.#key}:pending`) === form.id);
      if (form && (confirmed || this.refs.formErrors)) sessionStorage.removeItem(`${this.#key}:pending`);
    } catch {}
    const returnedHere = Boolean(form && (location.hash === `#${form.id}` || pending));
    if ((confirmed || this.refs.formErrors) && returnedHere) {
      this.refs.details.open = true;
    } else if (this.dataset.autoOpen === 'true' && this.dataset.designMode !== 'true' && !confirmed && !remembered.subscribed && Number(remembered.dismissedUntil || 0) < Date.now()) {
      this.#autoTimer = setTimeout(() => {
        const openOverlay = [...document.querySelectorAll('dialog[open], [aria-modal="true"], details[open]')].some((node) => {
          const rect = node.getBoundingClientRect();
          const style = getComputedStyle(node);
          return rect.width > 0 && rect.height > 0 && rect.top < innerHeight && rect.bottom > 0 && style.visibility !== 'hidden' && style.display !== 'none';
        });
        const busy = document.activeElement?.matches('input, textarea, select, [contenteditable="true"]') || openOverlay;
        if (!this.#autoOpened && this.isConnected && !document.hidden && !busy && this.#checkExpiry()) {
          this.#autoOpened = true;
          this.refs.details.open = true;
        }
      }, Number(this.dataset.delay || 4) * 1000);
    }
    if (this.refs.details.open) this.#openPanel();
  }

  #openPanel() {
    if (this.#modalOpen || !this.#checkExpiry()) return;
    clearTimeout(this.#autoTimer);
    this.#returnFocus = document.activeElement instanceof HTMLElement ? document.activeElement : this.refs.trigger;
    this.refs.panel.showModal();
    this.#modalOpen = true;
    lockScroll(this.refs.panel);
    this.refs.trigger.setAttribute('aria-expanded', 'true');
    this.#save({ dismissedUntil: Date.now() + Number(this.dataset.repeatDays) * 86400000 });
    // Start with context rather than opening a mobile keyboard before the offer is read.
    const target = this.refs.formErrors ? this.refs.email : this.refs.signup?.hidden ? this.refs.successText : this.refs.heading;
    (target || this.refs.heading).focus({ preventScroll: true });
  }

  /** @param {MouseEvent} event */
  #isBackdrop(event) {
    if (event.target !== this.refs.panel) return false;
    const rect = this.refs.panel.getBoundingClientRect();
    return event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom;
  }

  #checkExpiry() {
    clearTimeout(this.#expiryTimer);
    if (!this.dataset.expires?.trim()) return true;
    if (!Number.isFinite(this.#expires) || Date.now() >= this.#expires) {
      this.closeOffer();
      this.hidden = true;
      clearTimeout(this.#autoTimer);
      return false;
    }
    this.#expiryTimer = setTimeout(() => this.#checkExpiry(), Math.min(this.#expires - Date.now(), 60000));
    return true;
  }

  /** Close the offer and restore the focus held before it opened. */
  closeOffer() {
    const wasOpen = this.#modalOpen;
    this.#modalOpen = false;
    this.refs.details.open = false;
    this.refs.trigger.setAttribute('aria-expanded', 'false');
    if (this.refs.panel.open) this.refs.panel.close();
    unlockScroll(this.refs.panel);
    this.#save({ dismissedUntil: Date.now() + Number(this.dataset.repeatDays) * 86400000 });
    if (wasOpen) {
      const target = this.#returnFocus?.isConnected && !this.refs.panel.contains(this.#returnFocus) && this.#returnFocus !== document.body
        ? this.#returnFocus : this.refs.trigger;
      target.focus({ preventScroll: true });
    }
  }

  /** Copy only a code already revealed by the existing subscription workflow. */
  async copyCode() {
    const { success, code, copyButton, status } = this.refs;
    if (!success || !code || !copyButton || !status || !this.#checkExpiry() || success.hidden || !code.textContent.trim()) return;
    try {
      await navigator.clipboard.writeText(code.textContent.trim());
      copyButton.textContent = this.dataset.copiedLabel;
      status.textContent = this.dataset.copiedLabel;
      clearTimeout(this.#copyTimer);
      this.#copyTimer = setTimeout(() => { copyButton.textContent = this.dataset.copyLabel; }, 2000);
    } catch {
      status.textContent = this.dataset.copyFailed;
    }
  }
}

if (!customElements.get('mervaux-offer-component')) customElements.define('mervaux-offer-component', MervauxOffer);


